import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { prop } from "ramda"
import { compact, sortBy } from "lodash"
import type { StateObject } from "../../../redux/types"
import { getRecordEditId } from "../../../router/selectors"
import type { ModifiersAutoCompleteSelectionItem } from "../components/ModifierAndModifierGroupsAutoComplete"
import { sortByFrequencyAndFilter } from "../../../../utils/helpers"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import type { ProductPricingsTableItem } from "../components/ProductPricingsTable"
import type {
    CategoriesListItem,
    CategoryDetailData,
    CategoryDetailOverride,
    CategoryDetailResponse,
    CategoryModifierGroup,
    CategoryPricingOverride,
    CategoryTaxOverride,
    InItemModifier,
    InItemModifierGroup,
    InItemModifierOverride,
    InItemOption,
    InItemOptionOverride,
} from "../../../../graphql/types"
import { AggregatorType, CategoryType, TerminalType } from "../../../../graphql/types"
import type { CategoryFormData } from "./CategoryForm"
import type { ProductTaxesTableItem } from "../components/ProductTaxesTable"
import { computeOverriddenValue } from "../utils"
import type { AssignedModifierGroupTableItem } from "./AssignedModifierGroupTable"
import { getItemsAndCategoriesList, getItemsCategoriesState } from "../selectors"
import type { SingleChoiceDropdownItem } from "../../../common/components/SingleChoiceDropdown"
import { getAllModifierGroupsNew } from "../modifierGroups/selectors"
import { constructCategoryFormData, constructOverridesData } from "./utils"
import { ItemOverrideV2 } from "./detail/types"
import { getLocationsAuthData, getTerminalsAuthData } from "../../../auth/selectors"
import { TerminalAuthData } from "../../../auth/types"
import { getSettingValueByKeyAndLocationId } from "../../settings/selectors"
import { ItemDetailIncreasePriceInfo } from "./types"
import { ItemIsActiveLocationTableRow } from "./components/ItemIsActiveLocationTable"
import { ItemIsActiveLocationTaxesTableRow } from "./components/ItemIsActiveLocationTaxesTable"

export const getFrequentlyUsedUnits = (state: StateObject): string[] => {
    const items = getItemsAndCategoriesList(state)
    const units = items
        .filter(item => item.type === CategoryType.Category || item.type === CategoryType.Product)
        .map(item => item.unit)
    return sortByFrequencyAndFilter(compact(units)).slice(0, 5)
}

export const getEditCategoryFormItem = (state: StateObject): Maybe<CategoryFormData> => {
    const categoryResponse: Maybe<CategoryDetailResponse> = getEditCategory2(state)
    return constructCategoryFormData(categoryResponse)
}

export const getEditCategoryFormItemLocation = (state: StateObject): Maybe<CategoryFormData> => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const categoryResponse: Maybe<CategoryDetailResponse> = getEditCategory2(state)

    if (!categoryResponse) {
        return null
    }

    const category: CategoryDetailData = categoryResponse.data

    if (!category) {
        return null
    }

    const overrides: CategoryDetailOverride[] = categoryResponse.overrides
    const isActiveOverriddenValue = computeOverriddenValue(overrides, selectedLocationId, category.id, prop("isActive"))
    // TODO change on API to fixed price, not array
    const isPriceOverriddenValueOrigin = computeOverriddenValue(
        overrides,
        selectedLocationId,
        category.id,
        prop("price")
    )
    const isPriceOverriddenValue =
        // @ts-expect-error
        isPriceOverriddenValueOrigin != null && isPriceOverriddenValueOrigin.length > 0
            ? isPriceOverriddenValueOrigin[0]
            : null
    const locationOverrides = overrides.find(o => o.locationId === selectedLocationId)
    const overrideTaxes: CategoryTaxOverride[] = locationOverrides
        ? locationOverrides.data.taxes
            ? locationOverrides.data.taxes
            : []
        : []

    // @ts-expect-error
    const taxes: ProductTaxesTableItem[] = category.taxes.map(t => {
        const findOverride = overrideTaxes.find(ot => ot.id === t.id)
        return {
            id: t.id,
            name: t.name,
            isActive: findOverride && findOverride.isActive != null ? findOverride.isActive : t.isActive,
            isActiveOverriden: findOverride && findOverride.isActive != null && findOverride.isActive !== t.isActive,
            isAutoApply: findOverride && findOverride.isAutoApply != null ? findOverride.isAutoApply : t.isAutoApply,
            isAutoApplyOverriden:
                findOverride && findOverride.isAutoApply != null && findOverride.isAutoApply !== t.isAutoApply,
            isEmbedded: findOverride && findOverride.isEmbedded != null ? findOverride.isEmbedded : t.isEmbedded,
            isEmbeddedOverriden:
                findOverride && findOverride.isEmbedded != null && findOverride.isEmbedded !== t.isEmbedded,
            parentOverride: t.parentOverride,
        }
    })
    const overridePricings: CategoryPricingOverride[] = locationOverrides
        ? locationOverrides.data.pricings
            ? locationOverrides.data.pricings
            : []
        : []

    // @ts-expect-error
    const pricings: ProductPricingsTableItem[] = category.pricings.map(p => {
        const findOverride = overridePricings.find(op => op.id === p.id)
        return {
            id: p.id,
            name: p.name,
            isActive: findOverride && findOverride.isActive != null ? findOverride.isActive : p.isActive,
            isActiveOverriden: findOverride && findOverride.isActive != null && findOverride.isActive !== p.isActive,
            isAutoApply: findOverride && findOverride.isAutoApply != null ? findOverride.isAutoApply : p.isAutoApply,
            isAutoApplyOverriden:
                findOverride && findOverride.isAutoApply != null && findOverride.isAutoApply !== p.isAutoApply,
            parentOverride: p.parentOverride,
        }
    })
    const overrideModifiers: InItemModifierOverride[] = locationOverrides
        ? locationOverrides.data.modifiers
            ? locationOverrides.data.modifiers
            : []
        : []
    const overrideOptions: InItemOptionOverride[] = locationOverrides
        ? locationOverrides.data.options
            ? locationOverrides.data.options
            : []
        : []
    return {
        categoryId: category.id,
        // @ts-expect-error
        type: category.type,
        name: category.name,
        shortName: category.displayName,
        internalName: category.internalName,
        kitchenName: category.kitchenName,
        alternativeNames: category.alternativeNames,
        tags: category.tags,
        description: category.description,
        parentId: category.parentId,
        icon: category.icon,
        color: category.color,
        // @ts-expect-error
        isActive: isActiveOverriddenValue != null ? isActiveOverriddenValue : category.isActive,
        isActiveOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== category.isActive,
        price: handleLocationOverridePrice(isPriceOverriddenValue, category.fixedPrice, category.optionGroup),
        priceOverriden: isPriceOverriddenValue != null && isPriceOverriddenValue !== category.fixedPrice,
        newlyCreatedGroupId: null,
        categoryImagesNew: category.images || [],
        unit: category.unit,
        minQuantity: category.minQuantity,
        maxQuantity: category.maxQuantity,
        pieces: category.pieces,
        containsAlcohol: category.containsAlcohol != null ? category.containsAlcohol : false,
        alwaysOpenPopover: category.alwaysOpenPopover != null ? category.alwaysOpenPopover : false,
        mibNoteDisabled: category.disableItemNoteMiB,
        mibNoteRequired: category.requireItemNoteMiB,
        mibNotePlaceholder: category.itemNoteMiBPlaceholder,
        kioskNoteDisabled: category.disableItemNoteKiosk,
        displayImagePOS: category.displayImagePOS,
        categoryTaxes: sortBy(taxes, "name"),
        categoryPricings: pricings,
        cardProgramId: category.cardProgramId,
        overrides: getEditCategoryOverridesV2(state, category),
        modifierGroupsNew: category.modifierGroups.map(
            (group: InItemModifierGroup): AssignedModifierGroupTableItem => ({
                id: group.id,
                name: group.name,
                isProminent: group.isProminent,
                isExclusion: group.isExclusion,
                modifiers: group.modifiers.map((modifier: InItemModifier) => {
                    const findModifierOverride = overrideModifiers.find(om => om.id === modifier.id)
                    return {
                        id: modifier.id,
                        productId: modifier.productId,
                        name: modifier.name,
                        isPreset:
                            findModifierOverride && findModifierOverride.isPreset != null
                                ? findModifierOverride.isPreset
                                : modifier.isPreset,
                        isPresetOverriden: !!(
                            findModifierOverride &&
                            findModifierOverride.isPreset != null &&
                            findModifierOverride.isPreset !== modifier.isPreset
                        ),
                        isMulti:
                            findModifierOverride && findModifierOverride.isMulti != null
                                ? findModifierOverride.isMulti
                                : modifier.isMulti,
                        isMultiOverriden: !!(
                            findModifierOverride &&
                            findModifierOverride.isMulti != null &&
                            findModifierOverride.isMulti !== modifier.isMulti
                        ),
                        isActive: findModifierOverride?.isActive ?? modifier.isActive ?? true,
                        price:
                            findModifierOverride && findModifierOverride.price != null
                                ? findModifierOverride.price
                                : modifier.price,
                        priceOverriden: !!(
                            findModifierOverride &&
                            findModifierOverride.price != null &&
                            findModifierOverride.price !== modifier.price
                        ),
                        groupExtra:
                            findModifierOverride && findModifierOverride.supplement != null
                                ? findModifierOverride.supplement
                                : modifier.supplement,
                        groupExtraOverriden: !!(
                            findModifierOverride &&
                            findModifierOverride.supplement != null &&
                            findModifierOverride.supplement !== modifier.supplement
                        ),
                        nestedCategoryGroupId: modifier.nestedCategoryGroupId, // TODO handle override
                    }
                }),
            })
        ),
        optionGroupNew: category.optionGroup
            ? {
                  id: category.optionGroup.id,
                  name: category.optionGroup.name,
                  isProminent: category.optionGroup.isProminent,
                  options: category.optionGroup.options.map((option: InItemOption) => {
                      const findOptionOverride = overrideOptions.find(om => om.id === option.id)
                      return {
                          id: option.id,
                          productId: option.productId,
                          name: option.name,
                          isPreset:
                              findOptionOverride && findOptionOverride.isPreset != null
                                  ? findOptionOverride.isPreset
                                  : option.isPreset,
                          isPresetOverriden: !!(
                              findOptionOverride &&
                              findOptionOverride.isPreset != null &&
                              findOptionOverride.isPreset !== option.isPreset
                          ),
                          price:
                              findOptionOverride && findOptionOverride.price != null
                                  ? findOptionOverride.price / 100
                                  : option.price / 100,
                          priceOverriden: !!(
                              findOptionOverride &&
                              findOptionOverride.price != null &&
                              findOptionOverride.price !== option.price
                          ),
                          nestedCategoryGroupId: option.nestedCategoryGroupId, // TODO override
                          isActive: findOptionOverride?.isActive ?? option.isActive ?? true,
                      }
                  }),
              }
            : null,
    }
}

const handleLocationOverridePrice = (
    isPriceOverriddenValue: Maybe<number>,
    fixedPrice: Maybe<number>,
    optionGroup: Maybe<any>
): Maybe<number> => {
    if (optionGroup) {
        return null
    }

    return isPriceOverriddenValue != null ? isPriceOverriddenValue / 100 : fixedPrice != null ? fixedPrice / 100 : null
}

export const getNewCategoryIcon = (id: Maybe<string>, categories: CategoriesListItem[]): Maybe<number> => {
    const cat = categories.find(p => p.id === id)

    if (cat) {
        return cat.icon || getNewCategoryIcon(cat.parentId, categories)
    }

    return null
}

export const getNewCategoryColor = (id: Maybe<string>, categories: CategoriesListItem[]): Maybe<string> => {
    const cat = categories.find(p => p.id === id)

    if (cat) {
        return cat.color || getNewCategoryColor(cat.parentId, categories)
    }

    return null
}

export const getEditCategoryId = (state: StateObject) => getRecordEditId(state)

export const getEditCategory2 = (state: StateObject): Maybe<CategoryDetailResponse> =>
    getItemsCategoriesState(state).categoryDetails.find(detail => detail.data.id === getEditCategoryId(state))

export const getCategoryById = (state: StateObject, categoryId: string): Maybe<CategoryDetailResponse> =>
    getItemsCategoriesState(state).categoryDetails.find(detail => detail.data.id === categoryId)

export const getEditCategoryOverridesV2 = (state: StateObject, category: CategoryDetailData): ItemOverrideV2[] => {
    const overrides = getEditCategory2(state)?.overridesV2 ?? []
    const aggregatorTerminals = getAggregatorTerminalsForPriceOverride(state)

    return constructOverridesData(overrides, aggregatorTerminals, category)
}

export const getDataForModifierAutoComplete = (state: StateObject): ModifiersAutoCompleteSelectionItem[] => {
    return getAllModifierGroupsNew(state).map((group: CategoryModifierGroup) => {
        return {
            id: group.id,
            title: group.name,
            type: "Modifier Group",
        }
    })
}

export const getModifierGroupsDropdownItems = (state: StateObject): SingleChoiceDropdownItem[] => {
    return getAllModifierGroupsNew(state).map((group: CategoryModifierGroup) => {
        return {
            id: group.id,
            name: group.name,
        }
    })
}

export const getCategoryDetails = (state: StateObject): CategoryDetailResponse[] =>
    getItemsCategoriesState(state).categoryDetails

export const getCategoriesForCategorySelector = (state: StateObject): CategoriesListItem[] => {
    const categories = getItemsAndCategoriesList(state).filter(c => c.type === CategoryType.Category)
    return sortBy(categories, "name")
}

export const getAggregatorTerminalsForPriceOverride = (state: StateObject): TerminalAuthData[] => {
    const locationId = getSelectedLocationOverrideId(state)
    return getTerminalsAuthData(state).filter(t => {
        const isAggregator = t.type === TerminalType.Aggregator
        const isAllowedAggregator = [AggregatorType.Uber, AggregatorType.Grubhub].includes(t.aggregatorType)
        const isEmailApiService = !!t.aggregatorConfig?.grubhub?.address

        return t.locationId === locationId && isAggregator && isAllowedAggregator && !isEmailApiService
    })
}

export const getPriceIncreaseInfo = (state: StateObject): Maybe<ItemDetailIncreasePriceInfo> => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    if (!selectedLocationId) {
        return null
    }
    const isPriceIncreaseEnabled = getSettingValueByKeyAndLocationId(
        state,
        "PRICE_INCREASE_ENABLED",
        selectedLocationId
    )
    if (!isPriceIncreaseEnabled) {
        return null
    }

    return {
        isEnabledPos:
            getSettingValueByKeyAndLocationId(state, "PRICE_INCREASE_ENABLED_POS", selectedLocationId) ?? false,
        isEnabledMib:
            getSettingValueByKeyAndLocationId(state, "PRICE_INCREASE_ENABLED_MIB", selectedLocationId) ?? false,
        isEnabledKiosk:
            getSettingValueByKeyAndLocationId(state, "PRICE_INCREASE_ENABLED_KIOSK", selectedLocationId) ?? false,
        percentage:
            getSettingValueByKeyAndLocationId(state, "PRICE_INCREASE_PERCENTAGE_VALUE", selectedLocationId) ?? 0,
    }
}

export const getDataForIsActiveLocationTable = (state: StateObject): ItemIsActiveLocationTableRow[] => {
    const categoryDetail = getEditCategory2(state)
    const locations = getLocationsAuthData(state)

    return locations.map(l => {
        const locationOverride = categoryDetail ? categoryDetail.overrides.find(o => o.locationId === l.id) : null
        return {
            id: l.id,
            locationName: l.name,
            isActive: locationOverride?.data.isActive ?? categoryDetail?.data.isActive ?? true,
        }
    })
}

export const getDataForIsActiveLocationTaxesTable = (state: StateObject): ItemIsActiveLocationTaxesTableRow[] => {
    const categoryDetail = getEditCategory2(state)
    const locations = getLocationsAuthData(state)

    return locations.map(l => {
        const locationOverride = categoryDetail ? categoryDetail.overrides.find(o => o.locationId === l.id) : null
        return {
            id: l.id,
            locationName: l.name,
            isActive: locationOverride?.data.isActive ?? categoryDetail?.data.isActive ?? true,
            taxes:
                categoryDetail?.overrides
                    ?.find(o => o.locationId === l.id)
                    ?.data.taxes?.map(t => ({
                        id: t.id,
                        isActive: t.isActive ?? false,
                        isAutoApply: t.isAutoApply ?? false,
                    })) ?? [],
        }
    })
}

export const getDataForIsActiveLocationTableByCategoryId = (
    state: StateObject,
    categoryId: Maybe<string>
): ItemIsActiveLocationTableRow[] => {
    if (!categoryId) {
        return []
    }
    const categoryDetail = getCategoryById(state, categoryId)
    const locations = getLocationsAuthData(state)

    return locations.map(l => {
        const locationOverride = categoryDetail ? categoryDetail.overrides.find(o => o.locationId === l.id) : null
        return {
            id: l.id,
            locationName: l.name,
            isActive: locationOverride?.data.isActive ?? categoryDetail?.data.isActive ?? true,
        }
    })
}
